import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: 'telecom', label: 'Telecom Store' },
  { value: 'insurance', label: 'Insurance Agency' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'other', label: 'Other' },
];
const COLORS = ['#00FF99', '#007AFF', '#FFB800', '#FF3B30', '#B0B0B0', '#9B59B6'];
const emptyBiz = { name: '', type: 'telecom', description: '', color: '#00FF99', commercial_name: '', address: '', website: '', phone: '', email: '' };

export default function BusinessesPage() {
  const { hasRole } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBiz);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/businesses`, { withCredentials: true });
      setBusinesses(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/businesses/${editing}`, form, { withCredentials: true });
        toast.success('Business updated');
      } else {
        await axios.post(`${API}/businesses`, form, { withCredentials: true });
        toast.success('Business created');
      }
      setOpen(false); setEditing(null); setForm(emptyBiz);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this business?')) return;
    try {
      await axios.delete(`${API}/businesses/${id}`, { withCredentials: true });
      toast.success('Business deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const openEdit = (b) => {
    setForm({ name: b.name, type: b.type, description: b.description || '', color: b.color || '#00FF99', commercial_name: b.commercial_name || '', address: b.address || '', website: b.website || '', phone: b.phone || '', email: b.email || '' });
    setEditing(b.business_id);
    setOpen(true);
  };

  return (
    <div className="flex-1 p-8" data-testid="businesses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Businesses</h1>
          <p className="text-[#B0B0B0] text-sm mt-1">Manage your business units. Resend API keys are configured in the Admin Panel.</p>
        </div>
        {hasRole('admin') && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyBiz); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-business-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] rounded-md">
                <Plus size={16} className="mr-2" /> Add Business
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>{editing ? 'Edit Business' : 'New Business'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Business Name</Label>
                  <Input data-testid="business-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="My Business" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Type</Label>
                    <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                      <SelectTrigger data-testid="business-type-select" className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                        {TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-white hover:bg-[#1A1A1A]">{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Color</Label>
                    <div className="flex gap-2 mt-2">
                      {COLORS.map(c => (
                        <button key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                          className={`w-7 h-7 rounded-md border-2 transition-transform duration-200 ${form.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Description</Label>
                  <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Business description" />
                </div>
                <div className="border-t border-[rgba(255,255,255,0.12)] pt-3">
                  <h3 className="text-xs text-[#00FF99] font-bold mb-3">Footer / Contact Information</h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Commercial Name</Label>
                      <Input value={form.commercial_name} onChange={e => setForm(p => ({ ...p, commercial_name: e.target.value }))}
                        className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Display name for emails" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Phone</Label>
                        <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                          className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="+1 234 567" />
                      </div>
                      <div>
                        <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Email</Label>
                        <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                          className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="info@company.com" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Address</Label>
                      <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                        className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="123 Main St, City" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Website</Label>
                      <Input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                        className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="https://www.company.com" />
                    </div>
                  </div>
                </div>
                <Button data-testid="save-business-btn" onClick={handleSave} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
                  {editing ? 'Update Business' : 'Create Business'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {businesses.map((b, i) => (
          <div key={b.business_id} className={`bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5 animate-fade-in stagger-${(i % 4) + 1}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${b.color || '#00FF99'}20` }}>
                  <Building2 size={20} style={{ color: b.color || '#00FF99' }} />
                </div>
                <div>
                  <h3 className="text-white font-bold">{b.name}</h3>
                  <p className="text-xs text-[#808080]">{TYPES.find(t => t.value === b.type)?.label || b.type}</p>
                </div>
              </div>
              {hasRole('admin') && (
                <div className="flex gap-1">
                  <button onClick={() => openEdit(b)} className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-white transition-colors duration-200">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => handleDelete(b.business_id)} className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-[#FF3B30] transition-colors duration-200">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
            {b.description && <p className="text-sm text-[#B0B0B0] mb-3">{b.description}</p>}
            <div className="border-t border-[rgba(255,255,255,0.06)] pt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#808080]">Resend Email</span>
                <span className={`text-xs px-2 py-0.5 rounded ${b.resend_api_key ? 'bg-[#00FF99]/10 text-[#00FF99]' : 'bg-[#FFB800]/10 text-[#FFB800]'}`}>
                  {b.resend_api_key ? 'Configured' : 'Not set'}
                </span>
              </div>
            </div>
          </div>
        ))}
        {businesses.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#808080]">
            <Building2 size={32} className="mx-auto mb-3 opacity-50" />
            <p>No businesses yet. Add your first business to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
